import { without } from 'lodash';

import {
  getInitialAssetType,
  isProjectTypeInAdsCategory,
  isProjectTypeInProductDescriptionCategory,
  isProjectTypeInSocialCategory,
  PROJECT_TYPE,
} from './project';
import { AI_SUGGESTION_MODE, getAvailableSuggestionModes } from './generation';
import { isAssetGenerationAdvancedDisabled } from './asset';

export const ONBOARDING_STAGE = {
  welcomePopup: 'WelcomePopup',
  welcomeRetrialPopup: 'WelcomeRetrialPopup',
  commonProjectWalkthrough: 'CommonProjectWalkthrough',
  continuousOptimizationWalkthrough: 'ContinuousOptimizationWalkthrough',
  productDescriptionWalkthrough: 'ProductDescriptionWalkthrough',
  continuousOptimizationTutorial: 'ContinuousOptimizationTutorial',
  blogBuilderNewEditingUI: 'BlogBuilderNewEditingUI',
  shopifyWelcome: 'ShopifyWelcome',
  briefEditing: 'BriefEditing',
  briefEditingBlog: 'BriefEditingBlog', // deprecated
  briefEditingContinuousOptimization: 'BriefEditingContinuousOptimization',
  shopifyProductWalkthrough: 'ShopifyProductWalkthrough',
  workspaces: 'Workspaces',
  dataDrivenDocumentsPopup: 'DataDrivenDocumentsPopup',
  basicPlanIntro: 'BasicPlanIntro', // deprecated
  migratedFromStarter: 'MigratedFromStarter',
  confirmAddToCampaign: 'ConfirmAddToCampaign',
  documentVariations: 'DocumentVariations',
  wtmLimitation: 'WtmLimitation',
  accounts: 'Accounts',
  wtmAsset: 'WtmAsset',
  listVariations: 'ListVariations',
  documentSuggestedPersonasNotify: 'MoreSuggestedPersonasNotify',
  documentPromptWithUrl: 'DocumentPromptWithUrl',
  personaBadgeHint: 'personaBadgeHint',
  cip: 'Cip',
  wtmCipMigrationPopup: 'WtmCipMigrationPopup',
  checkoutPlanRoute: 'CheckoutPlanRoute',
};

const PROJECT_ONBOARDING_STAGE = [
  ONBOARDING_STAGE.commonProjectWalkthrough,
  ONBOARDING_STAGE.continuousOptimizationWalkthrough,
  ONBOARDING_STAGE.productDescriptionWalkthrough,
  ONBOARDING_STAGE.blogBuilderNewEditingUI,
  ONBOARDING_STAGE.briefEditing,
  ONBOARDING_STAGE.briefEditingContinuousOptimization,
  ONBOARDING_STAGE.shopifyProductWalkthrough,
];

export const isProjectOnboardingStage = (stage) => PROJECT_ONBOARDING_STAGE.includes(stage);

export const ONBOARDING_STAGE_STATUS = {
  pending: 'pending',
  finished: 'finished',
  skipped: 'skipped',
};

export const ONBOARDING_STEP = {
  // "CommonProjectWalkthrough" steps
  generationTemplates: 'generationTemplates',
  customTemplates: 'customTemplates',
  generationRewrite: 'generationRewrite',
  generationAdvanced: 'generationAdvanced',
  draftVariations: 'draftVariations',
  predictiveScore: 'predictiveScore',
  detailsVariationActions: 'detailsVariationActions',
  detailsPanel: 'detailsPanel',

  // "ContinuousOptimizationWalkthrough" steps
  coAssets: 'coAssets',
  coAssetTypeClassification: 'coAssetTypeClassification',
  coGenerateNewVariations: 'coGenerateNewVariations',
  coPreviews: 'coPreviews',
  coPredictiveScore: 'coPredictiveScore',
  coScoreHistogram: 'coScoreHistogram',
  coContinuousOptimizationToggle: 'coContinuousOptimizationToggle',

  // "ProductDescriptionWalkthrough" steps
  pdGenerationRewrite: 'pdGenerationRewrite',
  pdGenerationAdvanced: 'pdGenerationAdvanced',
  pdDraftVariations: 'pdDraftVariations',
  pdPredictiveScore: 'pdPredictiveScore',
  pdDetailsVariationActions: 'pdDetailsVariationActions',
  pdDetailsPanel: 'pdDetailsPanel',

  // BriefEditing
  beReminded: 'beReminded',
  beRemindedCO: 'beRemindedCO',

  // Shopify Product steps
  shGenerateNewVariations: 'shGenerateNewVariations',
  shAssetPreview: 'shAssetPreview',
  shScorePreview: 'shScorePreview',
  shScoreHistogram: 'shScoreHistogram',
  shContinuousOptimizationToggle: 'shContinuousOptimizationToggle',

  workspaces: 'workspaces',
  accounts: 'accounts',

  // Document steps
  documentVariationScore: 'documentVariationScore',
  documentSuggestedPersonasNotify: 'documentSuggestedPersonasNotify',
  documentPromptWithUrl: 'documentPromptWithUrl',

  // List variations
  listVariationActions: 'listVariationActions',

  // Asset actions
  wtmAssetActions: 'wtmAssetActions',

  // CIP
  cipBoost: 'cipBoost',
};

const ONBOARDING_STAGE_STEPS = {
  [ONBOARDING_STAGE.commonProjectWalkthrough]: [
    ONBOARDING_STEP.generationTemplates,
    ONBOARDING_STEP.customTemplates,
    ONBOARDING_STEP.generationRewrite,
    ONBOARDING_STEP.generationAdvanced,
    ONBOARDING_STEP.draftVariations,
    ONBOARDING_STEP.predictiveScore,
    ONBOARDING_STEP.detailsVariationActions,
    ONBOARDING_STEP.detailsPanel,
  ],
  [ONBOARDING_STAGE.continuousOptimizationWalkthrough]: [
    ONBOARDING_STEP.coAssets,
    ONBOARDING_STEP.coAssetTypeClassification,
    ONBOARDING_STEP.coGenerateNewVariations,
    ONBOARDING_STEP.coPreviews,
    ONBOARDING_STEP.coPredictiveScore,
    ONBOARDING_STEP.coScoreHistogram,
    ONBOARDING_STEP.coContinuousOptimizationToggle,
  ],
  [ONBOARDING_STAGE.productDescriptionWalkthrough]: [
    ONBOARDING_STEP.pdGenerationRewrite,
    ONBOARDING_STEP.pdGenerationAdvanced,
    ONBOARDING_STEP.pdDraftVariations,
    ONBOARDING_STEP.pdPredictiveScore,
    ONBOARDING_STEP.pdDetailsVariationActions,
    ONBOARDING_STEP.pdDetailsPanel,
  ],
  [ONBOARDING_STAGE.briefEditing]: [ONBOARDING_STEP.beReminded],
  [ONBOARDING_STAGE.briefEditingContinuousOptimization]: [ONBOARDING_STEP.beRemindedCO],
  [ONBOARDING_STAGE.shopifyProductWalkthrough]: [
    ONBOARDING_STEP.shGenerateNewVariations,
    ONBOARDING_STEP.shAssetPreview,
    ONBOARDING_STEP.shScorePreview,
    ONBOARDING_STEP.shScoreHistogram,
    ONBOARDING_STEP.shContinuousOptimizationToggle,
  ],
  [ONBOARDING_STAGE.workspaces]: [ONBOARDING_STEP.workspaces],
  [ONBOARDING_STAGE.accounts]: [ONBOARDING_STEP.accounts],
  [ONBOARDING_STAGE.listVariations]: [ONBOARDING_STEP.listVariationActions],
  [ONBOARDING_STAGE.documentVariations]: [ONBOARDING_STEP.documentVariationScore],
  [ONBOARDING_STAGE.wtmAsset]: [ONBOARDING_STEP.wtmAssetActions],
  [ONBOARDING_STAGE.documentSuggestedPersonasNotify]: [
    ONBOARDING_STEP.documentSuggestedPersonasNotify,
  ],
  [ONBOARDING_STAGE.cip]: [ONBOARDING_STEP.cipBoost],
  [ONBOARDING_STAGE.documentPromptWithUrl]: [ONBOARDING_STEP.documentPromptWithUrl],
};

const filterCommonGuidedStep = (steps, project) => {
  // We should not show "customTemplates" step for project/asset which don't support it
  const initialAssetType = getInitialAssetType(project);
  const availableModes = getAvailableSuggestionModes(
    project.type,
    initialAssetType,
    project.brief.type
  );
  if (!availableModes.includes(AI_SUGGESTION_MODE.guided)) {
    return without(steps, ONBOARDING_STEP.customTemplates);
  }

  return steps;
};

const withoutGenerationAdvancedStep = (steps, project) => {
  // We should not show "advanced" step for project/asset which don't support it
  const initialAssetType = getInitialAssetType(project);
  if (isAssetGenerationAdvancedDisabled(initialAssetType)) {
    return without(steps, ONBOARDING_STEP.generationAdvanced, ONBOARDING_STEP.pdGenerationAdvanced);
  }

  return steps;
};

export const getOnboardingStepsForStage = (stage, project) => {
  const steps = ONBOARDING_STAGE_STEPS[stage] || [];

  if (stage === ONBOARDING_STAGE.commonProjectWalkthrough) {
    const filtered = withoutGenerationAdvancedStep(steps, project);
    return filterCommonGuidedStep(filtered, project);
  }
  if (stage === ONBOARDING_STAGE.productDescriptionWalkthrough) {
    return withoutGenerationAdvancedStep(steps, project);
  }
  return steps;
};

const getOnboardingStage = (stages, stageName) => {
  if (!Array.isArray(stages)) {
    return null;
  }
  return stages.find((s) => s.name === stageName);
};

export const isOnboardingStagePending = (stages, stageName) => {
  const stage = getOnboardingStage(stages, stageName);
  return stage ? stage.status === ONBOARDING_STAGE_STATUS.pending : false;
};

export const isOnboardingStageFinished = (stages, stageName) => {
  const stage = getOnboardingStage(stages, stageName);
  return stage ? stage.status === ONBOARDING_STAGE_STATUS.finished : false;
};

export const isOnboardingStageSkipped = (stages, stageName) => {
  const stage = getOnboardingStage(stages, stageName);
  return stage ? stage.status === ONBOARDING_STAGE_STATUS.skipped : false;
};

export function isOnboardingStageDone(stages, stageName) {
  return (
    isOnboardingStageFinished(stages, stageName) || isOnboardingStageSkipped(stages, stageName)
  );
}

export const getOnboardingStageValue = (stages, stageName, defaultValue = null) => {
  const stage = getOnboardingStage(stages, stageName);
  return stage ? stage.value : defaultValue;
};

export const getWalkthroughForProjectType = (projectType) => {
  if (
    isProjectTypeInAdsCategory(projectType) ||
    isProjectTypeInSocialCategory(projectType) ||
    [
      PROJECT_TYPE.blogPost,
      PROJECT_TYPE.emailSubjectLine,
      PROJECT_TYPE.emailPromotional,
      PROJECT_TYPE.emailColdOutreach,
      PROJECT_TYPE.sms,
      PROJECT_TYPE.landingPageBuilder,
      PROJECT_TYPE.seoMetaDescription,
    ].includes(projectType)
  ) {
    return ONBOARDING_STAGE.commonProjectWalkthrough;
  }

  if (isProjectTypeInProductDescriptionCategory(projectType)) {
    return ONBOARDING_STAGE.productDescriptionWalkthrough;
  }

  return null;
};
